"use client";

import { persistentAtom } from "@nanostores/persistent";
import * as Sentry from "@sentry/nextjs";
import type { Endorsement, IUser } from "common/utils/UserUtils";
import { isEmpty } from "lodash-es";
import {
	getLandingPageForNewOrg,
	hashEmail,
	logoutRedirect,
} from "common/utils/auth/AuthUtils";
import AdminClient from "common/clients/AdminClient";
import AuthClient from "common/clients/AuthClient";
import { onMount } from "nanostores";
import { mutate } from "swr";
import { userSurveySetup } from "./SurveyStore";

export const defaultUser = {
	username: "loading",
	gateway: "",
	organization: "",
	organization_id: 0,
	deprecated_flagstaff_org_id: 0,
	endorsements: [],
	roles: [],
	traits: {},
	sub: "",
	exp: "",
	picture: "",
	feature_flags: [],
};

// not using persistentMap as user changes infrequently in a session
// single key seems much simpler.
export const user = persistentAtom<IUser>("whoami", defaultUser, {
	encode: JSON.stringify,
	decode: JSON.parse,
});

export const isOpteraAdmin = () => {
	return user.get().roles?.includes("optera:admin") || false;
};

export const hasWriteOperator = () => {
	return user.get().roles?.includes("write:operator") || false;
};

export const hasWriteUploadData = () => {
	return user.get().roles?.includes("write:upload_data") || false;
};

export const hasEndorsement = (endorsement: Endorsement) => {
	return user.get().endorsements?.includes(endorsement) || false;
};

onMount(user, () => {
	// Mount mode
	// because of preload in head of pages/_document.tsx or app/layout.tsx browser cache may be utilized
	refreshUser();
	return () => {
		// Disabled mode
	};
});

export const refreshUser = async (redirect = false) => {
	try {
		const userData = await AuthClient.whoami();

		if (redirect) {
			// Only safe on the client
			if (typeof window !== "undefined") {
				clearSession();
				const newPath = getLandingPageForNewOrg(
					window.location.pathname,
					userData
				);
				window.location.assign(newPath);
			}
		} else {
			user.set(userData);

			// Only set Sentry on client
			if (typeof window !== "undefined") {
				Sentry.setUser(userData);

				// Only hash + set GA user if we’re in the browser
				if (userData?.username) {
					const hashedEmail = await hashEmail(userData.username);
				}
			}

			if (!isEmpty(userData.traits)) {
				userSurveySetup(userData);
			}
		}

		return userData;
	} catch (error: any) {
		if (error.response?.status !== 401) {
			user.set({
				...defaultUser,
				username: "error",
			});
		} else {
			user.set({
				...defaultUser,
				username: "unauthenticated",
			});
		}
		return user.get();
	}
};

/**
 * empty managed client-side caches (SWR and localStorage) useful as part of logout
 * @param callback - most likely used for logoutRedirect("/", "/");
 */
export const clearSession = async (callback?: () => void | Promise<void>) => {
	mutate(() => true, undefined, { revalidate: false });
	localStorage.clear();
	await callback?.();
};

const checkUserValues = async () => {
	const facadeUser = await AdminClient.getUserAssociations();
	const currentUser = user.get();
	const cookieRoles = currentUser.roles?.sort() || [];
	const cookieCurrentOrg = Number(currentUser.organization_id);
	const cookieTraits = currentUser.traits || {};
	const dbOrgs =
		facadeUser.organizations?.map((item) => item.organization) || [];
	const dbRoles =
		facadeUser.roles?.flatMap((item) => item.permissions ?? []) || [];
	const dbTraits = facadeUser.traits || {};

	const uniqueRoles = Array.from(new Set(dbRoles)).sort();
	const isOpteraAdmin = uniqueRoles.includes("optera:admin");

	if (
		(!isOpteraAdmin && !dbOrgs.includes(cookieCurrentOrg)) ||
		cookieRoles.toString() !== uniqueRoles.toString() ||
		JSON.stringify(cookieTraits) !== JSON.stringify(dbTraits)
	) {
		alert("Your session has expired. Redirecting to login page...");
		logoutRedirect("/", "/");
	}
};

user.listen((newUser, oldUser) => {
	if (oldUser.organization_id != newUser.organization_id) {
		// NOTE: this mutates all swr caches - but could be more selective with: key => typeof key === 'string' && key.startsWith('/api'),
		mutate(() => true, undefined, { revalidate: true });
	}
	if (newUser.username && !oldUser.username) {
		setInterval(() => {
			checkUserValues();
		}, 600000);
	}
});
